import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Slider,
    TextField,
    MenuItem,
    Checkbox,
    ListItemText,
    IconButton,
    Stack,
    Tooltip,
    InputAdornment,
    FormControlLabel,
    LinearProgress
} from '@mui/material';
import { GlobalContext, Polygon, IconType } from '../../../context/context';
import { handleGenerateMeetingPoints, estimateGridPointCount, MEETING_POINT_COUNT_WARNING_THRESHOLD } from '../handlers/meetingPointsHandlers';

import CircularProgress from '@mui/material/CircularProgress';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';
const ROAD_TYPE_OPTIONS = [
    'motorway', 'trunk', 'primary', 'secondary', 'tertiary',
    'unclassified', 'residential', 'service', 'living_street', 'track', 'road'
];

interface IconOption {
    icon: React.ReactElement;
    value: IconType;
    labelKey: string;
}

const iconOptions: IconOption[] = [
    { icon: <Icon size={20} name="location-dot" />, value: 'default', labelKey: 'default' },
    { icon: <Icon size={20} name="star" />, value: 'star', labelKey: 'star' },
    { icon: <Icon size={20} name="location-dot" />, value: 'location', labelKey: 'location' },
    { icon: <Icon size={20} name="flag" />, value: 'flag', labelKey: 'flag' },
    { icon: <Icon size={20} d={ICON_FALLBACKS.LocalHospitalOutlined} />, value: 'hospital', labelKey: 'hospital' },
    { icon: <Icon size={20} d={ICON_FALLBACKS.SchoolOutlined} />, value: 'school', labelKey: 'school' },
    { icon: <Icon size={20} d={ICON_FALLBACKS.ShoppingCartOutlined} />, value: 'shopping', labelKey: 'shopping' },
    { icon: <Icon size={20} name="plane" />, value: 'airport', labelKey: 'airport' },
    { icon: <Icon size={20} name="bus" />, value: 'bus', labelKey: 'bus' },
    { icon: <Icon size={20} name="train" />, value: 'train', labelKey: 'train' },
    { icon: <Icon size={20} d={ICON_FALLBACKS.StoreOutlined} />, value: 'store', labelKey: 'store' },
    { icon: <Icon size={20} d={ICON_FALLBACKS.RestaurantOutlined} />, value: 'restaurant', labelKey: 'restaurant' },
    { icon: <Icon size={20} name="square-parking" />, value: 'parking', labelKey: 'parking' }
];

const colorPalette = [
    '#FF0000', // Red
    '#FF4500', // Orange Red
    '#FFA500', // Orange
    '#FFD700', // Gold
    '#FFFF00', // Yellow
    '#32CD32', // Lime Green
    '#008000', // Green
    '#00CED1', // Dark Turquoise
    '#0000FF', // Blue
    '#4B0082', // Indigo
    '#800080', // Purple
    '#FF1493', // Deep Pink
    '#A52A2A', // Brown
    '#000000', // Black
    '#808080', // Gray
    '#FFFFFF', // White
];

interface GenerateMeetingPointsModalProps {
    open: boolean;
    onClose: () => void;
    polygon: Polygon | null;
    global: GlobalContext;
    setMeetingPointsLoading: (loading: boolean) => void;
    setIsMeetingPointsModalOpen: (open: boolean) => void;
    setMeetingPointsPolygon: (polygon: Polygon | null) => void;
    loading?: boolean;
    onLayerCreated?: (layerId: number, layerName: string) => void;
}

export default function GenerateMeetingPointsModal({
    open,
    onClose,
    polygon,
    global,
    setMeetingPointsLoading,
    setIsMeetingPointsModalOpen,
    setMeetingPointsPolygon,
    loading = false,
    onLayerCreated
}: GenerateMeetingPointsModalProps) {
    const { t } = useTranslation(['mapDialogs', 'common']);
    const [distance, setDistance] = useState(400);
    const [roadTypes, setRoadTypes] = useState<string[]>(['residential', 'primary', 'secondary', 'tertiary']);
    const [selectedIcon, setSelectedIcon] = useState<IconType>('location');
    const [selectedColor, setSelectedColor] = useState('#ff6f00');
    const [iconSize, setIconSize] = useState(24);
    const [includeBusStops, setIncludeBusStops] = useState(true);
    const [excludeNearBusStops, setExcludeNearBusStops] = useState(true);
    const [exclusionDistance, setExclusionDistance] = useState(400);
    const [addressLookupProgress, setAddressLookupProgress] = useState<{
        current: number;
        total: number;
        message: string;
        estimatedTimeRemaining?: number;
        startTime?: number;
    } | null>(null);
    const [abortController, setAbortController] = useState<AbortController | null>(null);
    const [showCountWarning, setShowCountWarning] = useState(false);
    const [estimatedCount, setEstimatedCount] = useState<number | null>(null);

    const calculateEstimatedTimeRemaining = (
        current: number, 
        total: number, 
        startTime: number
    ): number => {
        if (current === 0) return 0;
        
        const elapsed = Date.now() - startTime;
        const averageTimePerItem = elapsed / current;
        const remaining = total - current;
        
        return Math.round(remaining * averageTimePerItem);
    };

    const formatTimeRemaining = (milliseconds: number): string => {
        if (milliseconds < 1000) return t('mapDialogs:meetingPoints.timeLessThanSec');

        const seconds = Math.round(milliseconds / 1000);
        if (seconds < 60) return t('mapDialogs:meetingPoints.timeSeconds', { count: seconds });

        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;

        if (minutes === 1 && remainingSeconds === 0) return t('mapDialogs:meetingPoints.timeOneMinute');
        if (remainingSeconds === 0) return t('mapDialogs:meetingPoints.timeMinutes', { count: minutes });

        return t('mapDialogs:meetingPoints.timeMinutesSeconds', { m: minutes, s: remainingSeconds });
    };

    const handleCancel = () => {
        if (abortController) {
            abortController.abort();
            setAbortController(null);
            setAddressLookupProgress(null);
        }
    };

    const handleSubmit = () => {
        if (!polygon) return;

        if (!showCountWarning) {
            const count = estimateGridPointCount(polygon, distance);
            if (count > MEETING_POINT_COUNT_WARNING_THRESHOLD) {
                setEstimatedCount(count);
                setShowCountWarning(true);
                return;
            }
        }

        setShowCountWarning(false);
        setEstimatedCount(null);

        if (polygon) {
            // Create new AbortController for this operation
            const controller = new AbortController();
            setAbortController(controller);
            
            // Reset progress state
            setAddressLookupProgress(null);
            
            void handleGenerateMeetingPoints({
                distance,
                roadTypes,
                selectedIcon,
                selectedColor,
                iconSize,
                includeBusStops,
                excludeNearBusStops,
                exclusionDistance,
                polygon,
                global,
                setMeetingPointsLoading,
                setIsMeetingPointsModalOpen,
                setMeetingPointsPolygon,
                onAddressLookupProgress: (current, total, message) => {
                    const now = Date.now();
                    const startTime = addressLookupProgress?.startTime || now;
                    const estimatedTimeRemaining = calculateEstimatedTimeRemaining(current, total, startTime);
                    
                    setAddressLookupProgress({ 
                        current, 
                        total, 
                        message,
                        estimatedTimeRemaining,
                        startTime
                    });
                },
                abortSignal: controller.signal,
                onLayerCreated,
            });
        }
    };

    const handleIconSizeChange = (newSize: number) => {
        if (newSize >= 12 && newSize <= 64) {
            setIconSize(newSize);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            sx={{ zIndex: 2500 }}
            PaperProps={{
                sx: {
                    zIndex: 2500,
                },
            }}
        >
            <DialogTitle>{t('mapDialogs:meetingPoints.title')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        {t('mapDialogs:meetingPoints.distanceLabel')}
                    </Typography>
                    <Slider
                        value={distance}
                        min={50}
                        max={1000}
                        step={10}
                        valueLabelDisplay="auto"
                        onChange={(_, v) => { setDistance(v as number); setShowCountWarning(false); }}
                        disabled={loading}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'flex-end' }}>
                        <TextField
                            label={t('mapDialogs:meetingPoints.distance')}
                            type="number"
                            value={distance}
                            onChange={e => { setDistance(Number(e.target.value)); setShowCountWarning(false); }}
                            inputProps={{ min: 50, max: 1000, step: 10 }}
                            disabled={loading}
                            sx={{ width: 180 }}
                        />
                        <TextField
                            label={t('mapDialogs:meetingPoints.roadTypes')}
                            select
                            SelectProps={{
                                multiple: true,
                                renderValue: (selected) => (selected as string[]).join(', '),
                                MenuProps: { sx: { zIndex: 2605 } },
                            }}
                            value={roadTypes}
                            onChange={e => setRoadTypes(e.target.value as unknown as string[])}
                            disabled={loading}
                            fullWidth
                        >
                            {ROAD_TYPE_OPTIONS.map(type => (
                                <MenuItem key={type} value={type}>
                                    <Checkbox checked={roadTypes.indexOf(type) > -1} />
                                    <ListItemText primary={type} />
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={includeBusStops}
                                    onChange={(e) => setIncludeBusStops(e.target.checked)}
                                    disabled={loading}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Icon size={20} name="bus" style={{ color: '#1976d2' }} />
                                    <Typography variant="body2">
                                        {t('mapDialogs:meetingPoints.includeBusStops')}
                                    </Typography>
                                </Box>
                            }
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={excludeNearBusStops}
                                    onChange={(e) => setExcludeNearBusStops(e.target.checked)}
                                    disabled={loading}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Icon size={20} name="bus" style={{ color: '#ff6b6b' }} />
                                    <Typography variant="body2">
                                        {t('mapDialogs:meetingPoints.excludeNearBusStops')}
                                    </Typography>
                                </Box>
                            }
                        />
                    </Box>

                    {excludeNearBusStops && (
                        <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {t('mapDialogs:meetingPoints.exclusionDistanceLabel')}
                            </Typography>
                            <Stack spacing={2} direction="row" alignItems="center">
                                <Slider
                                    value={exclusionDistance}
                                    onChange={(_, value) => setExclusionDistance(value as number)}
                                    min={25}
                                    max={500}
                                    step={25}
                                    sx={{ flexGrow: 1 }}
                                    disabled={loading}
                                />
                                <TextField
                                    value={exclusionDistance}
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value);
                                        if (!isNaN(value) && value >= 25 && value <= 500) {
                                            setExclusionDistance(value);
                                        }
                                    }}
                                    type="number"
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">m</InputAdornment>,
                                    }}
                                    sx={{ width: 140 }}
                                    size="small"
                                    disabled={loading}
                                />
                            </Stack>
                        </Box>
                    )}

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('mapDialogs:meetingPoints.iconSize')}
                        </Typography>
                        <Stack spacing={2} direction="row" alignItems="center">
                            <Slider
                                value={iconSize}
                                onChange={(_, value) => handleIconSizeChange(value as number)}
                                min={12}
                                max={64}
                                step={1}
                                sx={{ flexGrow: 1 }}
                                disabled={loading}
                            />
                            <TextField
                                value={iconSize}
                                onChange={(e) => {
                                    const value = parseInt(e.target.value);
                                    if (!isNaN(value)) {
                                        handleIconSizeChange(value);
                                    }
                                }}
                                type="number"
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">px</InputAdornment>,
                                }}
                                sx={{ width: 140 }}
                                size="small"
                                disabled={loading}
                            />
                        </Stack>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('mapDialogs:meetingPoints.iconStyle')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {iconOptions.map((option) => (
                                <Tooltip key={option.value} title={t(`mapDialogs:pointStyle.icon.${option.labelKey}`)}>
                                    <IconButton
                                        onClick={() => setSelectedIcon(option.value)}
                                        disabled={loading}
                                        sx={{
                                            border: selectedIcon === option.value ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                            color: selectedColor,
                                            width: 40,
                                            height: 40,
                                        }}
                                    >
                                        {option.icon}
                                    </IconButton>
                                </Tooltip>
                            ))}
                        </Box>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('mapDialogs:meetingPoints.color')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2, width: '100%' }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, flexGrow: 1 }}>
                                {colorPalette.map((color) => (
                                    <IconButton
                                        key={color}
                                        onClick={() => setSelectedColor(color)}
                                        disabled={loading}
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            backgroundColor: color,
                                            border: selectedColor === color ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                            '&:hover': {
                                                backgroundColor: color,
                                                opacity: 0.8,
                                            },
                                        }}
                                    />
                                ))}
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                minWidth: 60,
                                border: '2px solid #e0e0e0',
                                borderRadius: 2,
                                p: 1,
                                ml: 2,
                                background: '#fafbfc',
                            }}>
                                <Typography variant="caption" sx={{ mb: 0.5 }}>
                                    {t('mapDialogs:meetingPoints.customColor')}
                                </Typography>
                                <Box
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        backgroundColor: selectedColor,
                                        border: selectedColor.startsWith('#') && !colorPalette.includes(selectedColor)
                                            ? '2px solid #1976d2'
                                            : '1px solid #e0e0e0',
                                        borderRadius: 1,
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => {
                                        if (!loading) document.getElementById('custom-color-input')?.click();
                                    }}
                                />
                                <input
                                    id="custom-color-input"
                                    type="color"
                                    value={selectedColor}
                                    onChange={(e) => setSelectedColor(e.target.value)}
                                    style={{ display: 'none' }}
                                    disabled={loading}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Stack>
                
                {/* Large count warning */}
                {showCountWarning && estimatedCount !== null && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
                        <Typography variant="body2" fontWeight={600} color="warning.dark" gutterBottom>
                            {t('mapDialogs:meetingPoints.largeCountTitle')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <Trans
                                i18nKey="mapDialogs:meetingPoints.largeCountBody"
                                values={{ total: estimatedCount.toLocaleString() }}
                                components={{ strong: <strong /> }}
                            />
                        </Typography>
                    </Box>
                )}

                {/* Address Lookup Progress Bar */}
                {addressLookupProgress && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            {addressLookupProgress.message}
                        </Typography>
                        <LinearProgress 
                            variant="determinate" 
                            value={(addressLookupProgress.current / addressLookupProgress.total) * 100}
                            sx={{ 
                                height: 8, 
                                borderRadius: 4,
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 4,
                                }
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            {t('mapDialogs:meetingPoints.addressesProcessed', { current: addressLookupProgress.current, total: addressLookupProgress.total })}
                            {addressLookupProgress.estimatedTimeRemaining && addressLookupProgress.estimatedTimeRemaining > 0 && (
                                <span>{t('mapDialogs:meetingPoints.estRemaining', { value: formatTimeRemaining(addressLookupProgress.estimatedTimeRemaining) })}</span>
                            )}
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit" disabled={loading}>
                    {addressLookupProgress ? t('common:close') : t('common:cancel')}
                </Button>

                {/* Show cancel button during address lookup */}
                {addressLookupProgress && (
                    <Button
                        onClick={handleCancel}
                        color="warning"
                        variant="outlined"
                    >
                        {t('mapDialogs:meetingPoints.cancelAddressLookup')}
                    </Button>
                )}
                
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading || !polygon || !!addressLookupProgress}
                    startIcon={loading ? (
                        <CircularProgress
                            size={20}
                            sx={{
                                color: 'white',
                                '& .MuiCircularProgress-circle': {
                                    strokeLinecap: 'round',
                                }
                            }}
                        />
                    ) : null}
                    sx={{
                        py: 1.2,
                        px: 3,
                        fontSize: '1rem',
                        fontWeight: 600,
                        borderRadius: 2,
                        background: loading
                            ? 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #1976d2 100%)'
                            : '#1976d2',
                        backgroundSize: loading ? '200% 100%' : '100% 100%',
                        animation: loading ? 'shimmer 2s ease-in-out infinite' : 'none',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            background: loading
                                ? 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #1976d2 100%)'
                                : '#1565c0',
                            transform: loading ? 'none' : 'translateY(-1px)',
                            boxShadow: loading ? 'none' : '0 6px 20px rgba(25, 118, 210, 0.3)'
                        },
                        '&:disabled': {
                            color: 'white',
                            opacity: 0.9
                        },
                        '@keyframes shimmer': {
                            '0%': {
                                backgroundPosition: '-200% 0'
                            },
                            '100%': {
                                backgroundPosition: '200% 0'
                            }
                        }
                    }}
                >
                    {loading ? t('mapDialogs:meetingPoints.generating') : showCountWarning ? t('mapDialogs:meetingPoints.confirmAndGenerate') : t('mapDialogs:meetingPoints.generate')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
